import { useTranslation } from "react-i18next";
import { IconLoader2 } from "@tabler/icons-react";
import { Button } from "../../../components/ui/Button";
import { StepSearchInput } from "./StepSearchInput";
import { RegistryFooterActions } from "./RegistryFooterActions";
import {
  COUNTRY_SLIDE,
  PROFESSION_SLIDE,
  REGISTRY_SLIDE,
  SPECIALTY_SLIDE,
  type OnboardingFlow,
} from "../useOnboardingFlow";

/**
 * Footer CTA content of the onboarding carousel, shared by the mobile overlaid
 * footer band and the desktop footer: a registry match can be confirmed, while
 * a typed number can be searched or the profile can be completed manually.
 * Every other step gets the optional list search input + the generic Continue
 * (submitted through the step's form, so Enter works too). Rendered only when
 * `flow.showStepFooter` is true.
 */
export function OnboardingStepActions({
  flow,
  variant,
}: {
  flow: OnboardingFlow;
  /** `desktop` draws the buttons at the desktop screens' height (48). */
  variant: "mobile" | "desktop";
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const step = flow.step;

  if (step === REGISTRY_SLIDE && flow.registryPrimaryAction !== null)
    return <RegistryFooterActions flow={flow} variant={variant} />;

  // The grouped desktop form asks the country, the profession and the specialty
  // through dropdowns that carry their own search inside the panel. The band's
  // search field belongs to the full-screen lists, which only the carousel has.
  const showListSearch = !flow.groupsQuestions;

  return (
    <>
      {showListSearch && step === COUNTRY_SLIDE && (
        <StepSearchInput
          value={flow.countrySearch}
          onChange={flow.setCountrySearch}
          placeholder={t("Search countries")}
          variant={variant}
        />
      )}
      {showListSearch && step === PROFESSION_SLIDE && (
        <StepSearchInput
          value={flow.professionSearch}
          onChange={flow.setProfessionSearch}
          placeholder={t("Search profession")}
          variant={variant}
        />
      )}
      {showListSearch && step === SPECIALTY_SLIDE && (
        <StepSearchInput
          value={flow.specialtySearch}
          onChange={flow.setSpecialtySearch}
          placeholder={t("Narrow down my specialty")}
          variant={variant}
        />
      )}
      {/* Gated on `showPracticeSearch`, not on the step: that slide holds two
          questions and only the practice list is searchable. */}
      {flow.showPracticeSearch && (
        <StepSearchInput
          value={flow.practiceSearch}
          onChange={flow.setPracticeSearch}
          placeholder={flow.practiceSearchPlaceholder}
          variant={variant}
        />
      )}
      {/* Above the Continue of the axis question only. It is the one answer on
          this carousel that visibly narrows what comes next, so it is the one
          worth saying is reversible; the list behind it is edited from the same
          settings screen, and there the reader has already seen their whole
          catalogue. */}
      {flow.showPracticeReversibleNote && (
        <p className="m-0 text-center font-dm-sans text-ui-note text-ds-slate-500">
          {t("You can change this at any time in your profile.")}
        </p>
      )}
      {/* The last step finalizes the account without navigating anywhere, so
          the CTA itself carries the progress: it spins in place until the
          redirect lands. */}
      <Button
        type="submit"
        form={flow.stepFormId === null ? undefined : flow.stepFormId}
        variant={flow.isCurrentStepValid ? "primary" : undefined}
        size={variant === "mobile" ? "lg" : "md"}
        className="w-full"
        disabled={
          !flow.isCurrentStepValid ||
          flow.isFinalizing ||
          flow.accountCreationState.status === "running"
        }
        leftIcon={
          flow.isFinalizing ||
          flow.isSavingPartnerObjection ||
          flow.accountCreationState.status === "running" ? (
            <IconLoader2 className="animate-spin" />
          ) : null
        }
      >
        {t("Continue")}
      </Button>
    </>
  );
}
